import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import moment from 'moment-timezone';
import { CST_TIMEZONE, getCstToday, toCstStartOfDay, toCstEndOfDay } from '../../utils/cstDate';

const predefinedRanges = [
  { label: "Today", key: "today" },
  { label: "Yesterday", key: "yesterday" },
  { label: "This Week", key: "thisWeek" },
  { label: "Last Week", key: "lastWeek" },
  { label: "This Month", key: "thisMonth" },
  { label: "Last Month", key: "lastMonth" },
  { label: "This Year", key: "thisYear" },
  { label: "Last Year", key: "lastYear" },
  { label: "Custom Range", key: "custom" },
];

// Date utility functions (America/Chicago — independent of browser timezone)
const toCst = (date) => moment(date).tz(CST_TIMEZONE);
const toPickerDate = (value) => {
  const cst = toCst(value);
  return moment.tz(
    `${cst.format('YYYY-MM-DD')} 12:00`,
    'YYYY-MM-DD HH:mm',
    CST_TIMEZONE
  ).toDate();
};

const compareCstDays = (left, right) =>
  toCst(left).startOf('day').valueOf() - toCst(right).startOf('day').valueOf();

const format = (date, formatStr) => {
  const value = toCst(date);
  if (formatStr === "MMM yyyy") return value.format('MMM YYYY');
  if (formatStr === "MM/dd/yyyy") return value.format('MM/DD/YYYY');
  if (formatStr === "yyyy-MM-dd") return value.format('YYYY-MM-DD');
  if (formatStr === "d") return value.format('D');
  return value.format('ddd, MMM D YYYY');
};

const addMonths = (date, amount) => toCst(date).add(amount, 'month').toDate();
const subMonths = (date, amount) => toCst(date).subtract(amount, 'month').toDate();
const startOfMonth = (date) => toCst(date).startOf('month').toDate();
const endOfMonth = (date) => toCst(date).endOf('month').toDate();
const startOfWeek = (date) => toCst(date).startOf('week').toDate();
const endOfWeek = (date) => toCst(date).endOf('week').toDate();

const getPredefinedRange = (range) => {
  const today = toCst(getCstToday());

  switch (range) {
    case "today":
      return { start: toPickerDate(today), end: toPickerDate(today) };
    case "yesterday": {
      const yesterday = today.clone().subtract(1, 'day');
      return { start: toPickerDate(yesterday), end: toPickerDate(yesterday) };
    }
    case "thisWeek":
      return {
        start: toPickerDate(today.clone().startOf('week')),
        end: toPickerDate(moment.min(today.clone().endOf('week'), today)),
      };
    case "lastWeek": {
      const lastWeek = today.clone().subtract(1, 'week');
      return {
        start: toPickerDate(lastWeek.clone().startOf('week')),
        end: toPickerDate(lastWeek.clone().endOf('week')),
      };
    }
    case "thisMonth":
      return {
        start: toPickerDate(today.clone().startOf('month')),
        end: toPickerDate(moment.min(today.clone().endOf('month'), today)),
      };
    case "lastMonth": {
      const lastMonth = today.clone().subtract(1, 'month');
      return {
        start: toPickerDate(lastMonth.clone().startOf('month')),
        end: toPickerDate(lastMonth.clone().endOf('month')),
      };
    }
    case "thisYear":
      return {
        start: toPickerDate(today.clone().startOf('year')),
        end: toPickerDate(moment.min(today.clone().endOf('year'), today)),
      };
    case "lastYear": {
      const lastYear = today.clone().subtract(1, 'year');
      return {
        start: toPickerDate(lastYear.clone().startOf('year')),
        end: toPickerDate(lastYear.clone().endOf('year')),
      };
    }
    default:
      return { start: toPickerDate(today), end: toPickerDate(today) };
  }
};

const isSameMonth = (date1, date2) => toCst(date1).isSame(toCst(date2), 'month');
const isSameDay = (date1, date2) => toCst(date1).isSame(toCst(date2), 'day');

const isWithinInterval = (date, interval) => {
  const day = toCst(date).startOf('day');
  const start = toCst(interval.start).startOf('day');
  const end = toCst(interval.end).startOf('day');
  return day.isSameOrAfter(start, 'day') && day.isSameOrBefore(end, 'day');
};

const eachDayOfInterval = (interval) => {
  const days = [];
  let current = toCst(interval.start).startOf('day');
  const end = toCst(interval.end).startOf('day');

  while (current.isSameOrBefore(end, 'day')) {
    days.push(toPickerDate(current));
    current = current.add(1, 'day');
  }

  return days;
};

const DateRangePicker = ({ 
  isOpen, 
  onClose, 
  onApply, 
  initialStartDate, 
  initialEndDate 
}) => {
  const [currentMonth, setCurrentMonth] = useState(getCstToday());
  const [selectedRange, setSelectedRange] = useState("today");
  const [startDate, setStartDate] = useState(initialStartDate ? new Date(initialStartDate) : getCstToday());
  const [endDate, setEndDate] = useState(initialEndDate ? new Date(initialEndDate) : getCstToday());
  const [hoverDate, setHoverDate] = useState(null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);

  const nextMonth = addMonths(currentMonth, 1);

  useEffect(() => {
    if (initialStartDate) {
      setStartDate(new Date(initialStartDate));
    }
    if (initialEndDate) {
      setEndDate(new Date(initialEndDate));
    }
  }, [initialStartDate, initialEndDate]);

  useEffect(() => {
    if (selectedRange !== "custom") {
      const { start, end } = getPredefinedRange(selectedRange);
      setStartDate(start);
      setEndDate(end);
    }
  }, [selectedRange]);

  const handleDateClick = (date) => {
    if (selectedRange !== "custom") {
      setSelectedRange("custom");
    }

    if (!startDate || (startDate && endDate && !isSelectingEnd)) {
      setStartDate(toPickerDate(date));
      setEndDate(null);
      setIsSelectingEnd(true);
    } else if (startDate && !endDate) {
      const picked = toPickerDate(date);
      if (compareCstDays(picked, startDate) < 0) {
        setEndDate(startDate);
        setStartDate(picked);
      } else {
        setEndDate(picked);
      }
      setIsSelectingEnd(false);
    }
  };

  const handleRangeSelect = (range) => {
    setSelectedRange(range);
    if (range === "custom") {
      setStartDate(null);
      setEndDate(null);
      setIsSelectingEnd(false);
    }
  };

  const handleApply = () => {
    if (startDate && endDate) {
      const label = predefinedRanges.find((r) => r.key === selectedRange)?.label || "Custom Range";
      onApply(toCstStartOfDay(startDate), toCstEndOfDay(endDate), label);
      onClose();
    }
  };

  const renderCalendar = (month) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">{format(month, "MMM yyyy")}</h3>
          {month.getTime() === currentMonth.getTime() && (
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 p-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const isCurrentMonth = isSameMonth(day, month);
            const isSelected = startDate && endDate && isWithinInterval(day, { start: startDate, end: endDate });
            const isStart = startDate && isSameDay(day, startDate);
            const isEnd = endDate && isSameDay(day, endDate);
            const isInHoverRange =
              startDate &&
              hoverDate &&
              !endDate &&
              isWithinInterval(day, {
                start: compareCstDays(startDate, hoverDate) <= 0 ? startDate : hoverDate,
                end: compareCstDays(startDate, hoverDate) <= 0 ? hoverDate : startDate,
              });

            return (
              <button
                key={index}
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => setHoverDate(day)}
                onMouseLeave={() => setHoverDate(null)}
                className={`
                  h-8 w-8 text-sm rounded flex items-center justify-center transition-colors
                  ${!isCurrentMonth ? "text-gray-300" : "text-gray-900"}
                  ${isSelected || isInHoverRange ? "bg-red-100" : ""}
                  ${isStart || isEnd ? "bg-red-500 text-white" : ""}
                  ${isCurrentMonth && !isSelected && !isStart && !isEnd ? "hover:bg-gray-100" : ""}
                `.trim()}
              >
                {format(day, "d")}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${format(startDate, "MM/dd/yyyy")} - ${format(endDate, "MM/dd/yyyy")}`;
    }
    return "";
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex">
          {/* Sidebar */}
          <div className="w-48 bg-gray-50 border-r">
            <div className="p-4">
              {predefinedRanges.map((range) => (
                <button
                  key={range.key}
                  onClick={() => handleRangeSelect(range.key)}
                  className={`
                    w-full text-left px-3 py-2 text-sm rounded mb-1 transition-colors
                    ${
                      selectedRange === range.key
                        ? range.key === "custom"
                          ? "bg-yellow-400 text-black font-medium"
                          : "bg-blue-100 text-blue-900"
                        : "hover:bg-gray-100"
                    }
                  `.trim()}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar */}
          <div className="flex-1">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Select Date Range</h2>
                <p className="text-xs text-gray-500 mt-1">All dates use CST (America/Chicago)</p>
              </div>
              <button
                onClick={onClose}
                className="h-6 w-6 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex">
              {renderCalendar(currentMonth)}
              {renderCalendar(nextMonth)}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <input
                value={formatDateRange()}
                readOnly
                className="w-64 px-3 py-2 border border-gray-300 rounded-md bg-white"
                placeholder="Select date range"
              />
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={!startDate || !endDate}
                  className="px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-black rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;